import React, { useState, useContext } from 'react';
import {
  Bell,
  CheckCheck,
  AlertTriangle,
  MessageSquare,
  Megaphone,
  FileCheck,
  FileX,
  Building2,
  Users,
  Cake,
  Dog,
  Hand,
  CheckCircle2,
  LogOut,
  Ticket,
  UserCheck,
  Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { NotificationContext } from '@/contexts/NotificationContext';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { useNavigation } from '@/contexts/NavigationContext';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { downloadContactExportFromNotification } from '@/lib/notifications/contactExportDownload';

interface NotificationItemProps {
  notification: {
    id: string;
    type: string;
    title: string;
    message: string;
    data?: any;
    isRead: boolean;
    createdAt: string;
  };
  onMarkAsRead: (id: string) => void;
  onNavigate?: (notification: any) => void;
}

const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onMarkAsRead,
  onNavigate,
}) => {

  const handleClick = () => {
    if (onNavigate) {
      onNavigate(notification);
    }
  };

  const getNotificationIcon = (type: string): React.ComponentType<{ className?: string }> => {
    switch (type) {
      case 'CAMPAIGN_CREATED':
      case 'CAMPAIGN_COMPLETED':
      case 'CAMPAIGN_COMPLETED_WITH_ERRORS':
      case 'CAMPAIGN_SCHEDULED':
      case 'CAMPAIGN_STARTED':
      case 'CAMPAIGN_RETRY_FAILED':
        return Megaphone;
      case 'MESSAGE_RECEIVED':
        return MessageSquare;
      case 'TEMPLATE_APPROVED':
        return FileCheck;
      case 'TEMPLATE_REJECTED':
        return FileX;
      case 'BUSINESS_ACCOUNT_STATUS_CHANGE':
        return Building2;
      case 'TEAM_INVITATION':
        return Users;
      // New notification types for Muttley Crew
      case 'cake_order_form':
      case 'cake_interest':
        return Cake;
      case 'treats_interest':
        return Dog;
      case 'chat_request':
        return MessageSquare;
      case 'customer_interaction':
        return Hand;
      // COMMENTED OUT: Payment generation feature not in use
      // case 'payment_link':
      //   return CreditCard;
      case 'order_confirmed':
        return CheckCircle2;
      case 'session_ended':
        return LogOut;
      case 'TICKET_ASSIGNED':
        return Ticket;
      case 'TICKET_COMMENT':
        return MessageSquare;
      case 'CONVERSATION_ASSIGNED':
        return UserCheck;
      case 'SYSTEM_ALERT':
        return AlertTriangle;
      case 'SYSTEM_INFO':
        return Info;
      default:
        return Bell;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'CAMPAIGN_CREATED':
        return 'text-emerald-600';
      case 'CAMPAIGN_COMPLETED':
        return 'text-green-600';
      case 'CAMPAIGN_COMPLETED_WITH_ERRORS':
        return 'text-orange-600';
      case 'MESSAGE_RECEIVED':
        return 'text-blue-600';
      case 'TEMPLATE_APPROVED':
        return 'text-green-600';
      case 'TEMPLATE_REJECTED':
        return 'text-red-600';
      case 'BUSINESS_ACCOUNT_STATUS_CHANGE':
        return 'text-purple-600';
      case 'TEAM_INVITATION':
        return 'text-indigo-600';
      // New notification types for Muttley Crew
      case 'cake_order_form':
        return 'text-pink-600';
      case 'cake_interest':
        return 'text-pink-600';
      case 'treats_interest':
        return 'text-amber-600';
      case 'chat_request':
        return 'text-blue-600';
      case 'customer_interaction':
        return 'text-green-600';
      // COMMENTED OUT: Payment generation feature not in use
      // case 'payment_link':
      //   return 'text-emerald-600';
      case 'order_confirmed':
        return 'text-green-600';
      case 'session_ended':
        return 'text-gray-600';
      case 'TICKET_ASSIGNED':
        return 'text-indigo-600';
      case 'TICKET_COMMENT':
        return 'text-blue-600';
      case 'CONVERSATION_ASSIGNED':
        return 'text-teal-600';
      case 'SYSTEM_ALERT':
        return 'text-red-700 font-bold';
      case 'SYSTEM_INFO':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  const requiresAction = (type: string) => {
    return ['cake_order_form', 'chat_request', 'cake_interest'].includes(type);
  };

  const Icon = getNotificationIcon(notification.type);

  return (
    <div
      className={cn(
        "group flex items-start gap-3 p-3 hover:bg-gray-50 transition-colors cursor-pointer",
        !notification.isRead && "bg-green-50"
      )}
      onClick={() => {
        if (!notification.isRead) {
          onMarkAsRead(notification.id);
        }
        if (onNavigate) {
          onNavigate(notification);
        }
      }}
    >
      <div className="flex-shrink-0 mt-0.5">
        <Icon className="h-5 w-5 text-gray-600" />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {notification.title}
              </p>
              {requiresAction(notification.type) && (
                <Badge variant="destructive" className="text-xs px-1.5 py-0.5">
                  Action Required
                </Badge>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1 line-clamp-2">
              {notification.message}
            </p>

            {/* Labeled detail rows for ticket notifications */}
            {(notification.type === 'TICKET_ASSIGNED' || notification.type === 'TICKET_COMMENT') && (() => {
              const d = notification.data as any;
              return (
                <div className="mt-1.5 space-y-0.5">
                  {d?.ticketNumber && (
                    <div className="flex gap-1 text-xs">
                      <span className="text-gray-400 shrink-0">ID/Num:</span>
                      <span className="text-indigo-600 font-medium">T-{d.ticketNumber}</span>
                    </div>
                  )}
                  {d?.contactName && (
                    <div className="flex gap-1 text-xs">
                      <span className="text-gray-400 shrink-0">Customer:</span>
                      <span className="text-gray-600 line-clamp-1">{d.contactName}</span>
                    </div>
                  )}
                  {d?.subject && (
                    <div className="flex gap-1 text-xs">
                      <span className="text-gray-400 shrink-0">Description:</span>
                      <span className="text-gray-600 line-clamp-1">{d.subject}</span>
                    </div>
                  )}
                </div>
              );
            })()}

            <p className="text-xs text-gray-400 mt-1">
              {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const NotificationDropdown: React.FC<{ triggerIcon?: React.ReactNode }> = ({ triggerIcon }) => {
  // Debug logging removed to prevent console spam
  // Uncomment only when debugging notification issues
  // void(0);

  // Safely get notification context (may be undefined for admin/agency routes)
  const notificationContext = useContext(NotificationContext);
  const notifications = notificationContext?.notifications || [];
  // Bell badge is derived from the SAME list the dropdown renders so the two
  // can never disagree. Previously `unreadCount` was a separate state bumped
  // by the socket / server response, which drifted from `notifications` when
  // BA scope changed, a notification was filtered out server-side, or a
  // markAsRead happened on one side without invalidating the other (seen
  // 2026-05-12 — bell showed "1" while the dropdown said "You're all caught
  // up"). Source-of-truth: the visible list.
  const unreadCount = notifications.filter((n) => !n.isRead).length;
  const isLoading = notificationContext?.isLoading || false;
  const markAsRead = notificationContext?.markAsRead || (async () => { });
  const markAllAsRead = notificationContext?.markAllAsRead || (async () => { });

  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { interceptedNavigate } = useNavigation();
  const { selectedAccountId, setSelectedAccountId } = useBusinessAccountContext();

  // If notification context is not available, don't render the dropdown
  if (!notificationContext) {
    return (
      <div className="flex-shrink-0">
        {triggerIcon || <Bell className="w-4 h-4 sm:w-5 sm:h-5" />}
      </div>
    );
  }

  // Debug logging removed to prevent console spam
  // Uncomment only when debugging notification issues
  // void(0);

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleNotificationClick = async (notification: any) => {
    setIsOpen(false); // Close dropdown

    const data = notification.data || {};

    // Switch business account if the notification belongs to a different one
    const notificationAccountId = notification.businessAccountId || notification.business_account_id;
    if (notificationAccountId && notificationAccountId !== selectedAccountId) {
      setSelectedAccountId(notificationAccountId);
    }

    // Board stage auto-send (drip) failure alerts — fired by BoardService when
    // a template send fails (dead WhatsApp connection / undeliverable number /
    // missing media). Backend `type` is a
    // generic value, so route on `data.kind` + `data.deep_link` BEFORE the
    // switch (e.g. connection failures → /home/business-accounts to reconnect).
    if (data?.kind === 'board_auto_send_failure' && typeof data.deep_link === 'string') {
      interceptedNavigate(data.deep_link);
      return;
    }

    // Contact export ready — download the file (refresh presigned URL via jobId).
    // Must run BEFORE the generic deep_link branch, which would only navigate
    // to /home/leads|/home/customers and skip the download.
    if (
      data?.kind === 'contact_export_result' &&
      notification.type === 'CONTACT_EXPORT_COMPLETED'
    ) {
      const downloaded = await downloadContactExportFromNotification(data);
      if (downloaded) return;
      // Expired / missing URL — fall through to deep_link navigation
    }

    // GENERIC completion-notification routing — any notification whose data
    // carries an app-relative `deep_link` navigates straight to that resource.
    // This is the single extension point for scheduled/background operations
    // (scheduled posts, scheduled messages, syncs, exports, future jobs): the
    // backend attaches `data.deep_link` via `operationNotifications.js` and
    // routing works with ZERO per-feature frontend changes. Placed AFTER the
    // kind-specific branches above (which stay authoritative for their kinds)
    // and BEFORE the type switch so backend-declared destinations win over
    // the legacy per-type guesses. Only app-relative paths are honored —
    // never navigate to an absolute/external URL from notification data.
    if (
      typeof data?.deep_link === 'string' &&
      data.deep_link.startsWith('/') &&
      !data.deep_link.startsWith('//') // '/x' only — '//host' is protocol-relative, i.e. external
    ) {
      interceptedNavigate(data.deep_link);
      return;
    }

    // Navigate based on notification type, deep-linking to the specific resource
    switch (notification.type) {
      case 'CAMPAIGN_CREATED':
      case 'CAMPAIGN_COMPLETED':
      case 'CAMPAIGN_COMPLETED_WITH_ERRORS':
      case 'CAMPAIGN_SCHEDULED':
      case 'CAMPAIGN_STARTED':
      case 'CAMPAIGN_RETRY_FAILED':
        if (data.campaignId) {
          interceptedNavigate(`/home/campaigns?campaignId=${encodeURIComponent(data.campaignId)}`);
        } else {
          interceptedNavigate('/home/campaigns');
        }
        break;
      case 'MESSAGE_RECEIVED':
      case 'chat_request':
      case 'customer_interaction':
      case 'cake_order_form':
      case 'cake_interest':
      case 'treats_interest':
      // COMMENTED OUT: Payment generation feature not in use
      // case 'payment_link':
      case 'order_confirmed':
      case 'session_ended': {
        // Deep-link to the specific conversation in inbox
        const conversationId = data.conversationId || data.conversation_id;
        if (conversationId) {
          interceptedNavigate(`/home/inbox?conversationId=${conversationId}`, {
            state: {
              selectedConversationId: conversationId,
              contactName: data.contactName || data.contact_name,
              targetMessageId: data.messageId || data.message_id || null,
            },
          });
        } else {
          // Fallback for older notifications without conversationId in data:
          // Extract contact name from message (format: "New message from <name>: <preview>")
          const messageMatch = notification.message?.match(/^New message from (.+?):/);
          const contactName = data.contactName || data.contact_name || (messageMatch ? messageMatch[1] : null);
          interceptedNavigate('/home/inbox', {
            state: {
              searchContactName: contactName,
            },
          });
        }
        break;
      }
      case 'TEMPLATE_APPROVED':
      case 'TEMPLATE_REJECTED':
        interceptedNavigate('/home/templates');
        break;
      case 'BUSINESS_ACCOUNT_STATUS_CHANGE':
        interceptedNavigate('/home/settings');
        break;
      case 'TEAM_INVITATION':
        interceptedNavigate('/home/team-management');
        break;
      case 'TICKET_ASSIGNED':
      case 'TICKET_COMMENT': {
        const ticketId = data.ticketId || data.ticket_id;
        if (ticketId) {
          interceptedNavigate(`/home/tickets?ticketId=${encodeURIComponent(ticketId)}`);
        } else {
          interceptedNavigate('/home/tickets');
        }
        break;
      }
      case 'CONVERSATION_ASSIGNED': {
        const convId = data.conversationId || data.conversation_id;
        if (convId) {
          interceptedNavigate(`/home/inbox?conversationId=${convId}`, {
            state: {
              selectedConversationId: convId,
              contactName: data.contactName || data.contact_name || null,
            },
          });
        } else {
          interceptedNavigate('/home/inbox');
        }
        break;
      }
      case 'SYSTEM_NOTIFICATION':
      case 'SYSTEM_ALERT':
      case 'SYSTEM_INFO':
        interceptedNavigate('/home/notifications');
        break;
      case 'REMINDER': {
        // Deep-link to the conversation/contact the reminder was attached to,
        // or the My Reminders settings tab if the reminder was standalone.
        // Mirrors the backend's notification.data shape from
        // ReminderService.fireReminder.
        const conversationId = data.conversationId || data.conversation_id;
        const contactId = data.contactId || data.contact_id;
        if (conversationId) {
          interceptedNavigate(`/home/inbox?conversationId=${conversationId}`, {
            state: { selectedConversationId: conversationId },
          });
        } else if (contactId) {
          // Canonical lead URL uses ?contactId=<id> (query string), NOT
          // /home/contacts/:id (path style) — the path form has no matching
          // <Route> in App.tsx and falls through to the 404 page.
          // `tab=reminders` deep-links into the Reminders tab so the user
          // immediately sees the entry that fired (instead of the default
          // Activities tab which is usually empty).
          interceptedNavigate(
            `/home/contacts?contactId=${contactId}&tab=reminders`
          );
        } else {
          interceptedNavigate('/home/settings?tab=reminders');
        }
        break;
      }
      default:
        interceptedNavigate('/home/notifications');
        break;
    }
  };

  // Dropdown only shows unread notifications — read ones disappear
  // Dedupe by id before rendering — a socket push + a list refetch can briefly
  // deliver the same notification twice; without this guard React would warn on
  // duplicate keys and the row would render twice.
  const seenIds = new Set<string>();
  const recentNotifications = [...notifications]
    .filter(n => !n.isRead)
    .filter(n => {
      if (seenIds.has(n.id)) return false;
      seenIds.add(n.id);
      return true;
    })
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 10);

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative cursor-pointer p-[8px] w-[40px] h-[40px] rounded-[47px] bg-[#F5F7F9] ">
          {triggerIcon ? triggerIcon : <Bell className="h-5 w-5" />}
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-[10px]"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-80 sm:w-[400px] p-0 ml-[60px] sm:ml-[30px]">
        <DropdownMenuLabel className="p-4 pb-2">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Notifications</h3>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAllAsRead}
                className="text-xs"
              >
                <CheckCheck className="h-3 w-3 mr-1" />
                Mark all read
              </Button>
            )}
          </div>
        </DropdownMenuLabel>

        <Separator />

        <ScrollArea className="h-96">
          {isLoading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
            </div>
          ) : recentNotifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-gray-500">
              <Bell className="w-8 h-8 mb-2 text-muted-foreground" />
              <p className="text-sm">You're all caught up</p>
              <p className="text-xs text-gray-400 mt-1">No unread notifications</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {recentNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={markAsRead}
                  onNavigate={handleNotificationClick}
                />
              ))}
            </div>
          )}
        </ScrollArea>

        <Separator />
        <div className="p-2">
          <Button
            className="w-full"
            onClick={() => {
              interceptedNavigate('/home/notifications');
              setIsOpen(false);
            }}
          >
            View all notifications
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
